import { Stack, Switch, Typography } from '@vapor/material'
import { useTranslation } from 'react-i18next'
import { HicIconButton } from '../../hic-vapor/button/icon-button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import ConfigureNotificationCrud from './configure-notification.crud'
import { useProperty } from 'src/app/hooks/context/context.hook'
import { FormItem } from 'hic-components'
import { NcsChannel } from 'src/api/api.generated'
import { NotificationType } from '../notification-bell.type'
import BellLoading from './bell-loading'

const TEMPLATES_TO_SHOW = [NotificationType.ALERT, NotificationType.INFO, NotificationType.RESERVATION]

export default function ConfigureNotification({ onBack, userId }: { onBack: () => void; userId: string | undefined }) {
  const { t } = useTranslation()
  const property = useProperty()
  const tenantId = property?.tenantId ?? ''
  const propertyId = property?.id ?? ''
  const { nscUserPreferences, setNcsUserPreference, loading } = ConfigureNotificationCrud({
    propertyId,
    tenantId,
    userId,
  })

  function getTemplateTranslated(template: NotificationType) {
    switch (template) {
      case NotificationType.ALERT:
        return t('pms.notifications.configuration.templates.alert')
      case NotificationType.INFO:
        return t('pms.notifications.configuration.templates.info')
      case NotificationType.RESERVATION:
        return t('pms.notifications.configuration.templates.reservation')
      case NotificationType.MESSAGE:
        return t('pms.notifications.configuration.templates.message')
      default:
        return template
    }
  }

  function updatePreference({ channelToAdd, channels, template }: { channelToAdd: NcsChannel; channels: NcsChannel[]; template: string }) {
    if (channels?.includes(channelToAdd)) {
      const index = channels.indexOf(channelToAdd)
      if (index > -1) {
        channels.splice(index, 1)
      }
    } else {
      channels.push(channelToAdd)
    }
    const preferences = TEMPLATES_TO_SHOW.map((templateToShow) => {
      if (templateToShow === template) {
        return {
          channels,
          template,
        }
      }

      const preference = nscUserPreferences.find((preferenceToFind) => preferenceToFind.template === templateToShow)

      return { template: templateToShow, channels: preference?.channels ?? [] }
    })

    setNcsUserPreference({
      variables: {
        tenantId,
        propertyId,
        userId: userId ?? '',
        preferences: preferences,
      },
    })
  }

  return (
    <Stack>
      {loading && <BellLoading />}
      <Stack direction="row" alignItems="center" gap={0.5}>
        <HicIconButton sx={{ minWidth: '0px!important', minHeight: '0px!important', padding: 1 }} onClick={onBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </HicIconButton>
        <Typography variant="titleSmall">{t('pms.notifications.configuration.title')}</Typography>
      </Stack>
      {TEMPLATES_TO_SHOW?.map((template) => {
        const preference = nscUserPreferences.find((preferenceToFind) => preferenceToFind.template === template)

        return (
          <Stack key={template} direction="row" justifyContent="space-between" alignItems="center" sx={{ borderBottom: '1px solid var(--Gainsboro)' }}>
            <Typography>{getTemplateTranslated(template)}</Typography>
            <Stack direction="row" gap={1} alignItems="center" sx={{ borderLeft: '1px solid var(--Gainsboro)', padding: 1 }}>
              <FormItem fieldLabel={t('pms.notifications.configuration.email')}>
                <Switch
                  size="small"
                  checked={preference?.channels?.includes(NcsChannel.EMAIL) ?? false}
                  onChange={() => {
                    updatePreference({
                      channelToAdd: NcsChannel.EMAIL,
                      channels: [...(preference?.channels ?? [])],
                      template: template,
                    })
                  }}
                />
              </FormItem>
              <FormItem fieldLabel={t('pms.notifications.configuration.web')}>
                <Switch
                  size="small"
                  checked={preference?.channels?.includes(NcsChannel.WEB) ?? false}
                  onChange={() => {
                    updatePreference({
                      channelToAdd: NcsChannel.WEB,
                      channels: [...(preference?.channels ?? [])],
                      template: template,
                    })
                  }}
                />
              </FormItem>
            </Stack>
          </Stack>
        )
      })}
    </Stack>
  )
}
